import React, { useEffect, useMemo, useState } from 'react';
import ServicePopup from './ServicePopup';

function VIPLevelsPage({ onBack, userProfile }) {
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [selectedVipLevel, setSelectedVipLevel] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // VIP level requirements
  const vipRequirements = {
    'VIP1': 100,
    'VIP2': 500,
    'VIP3': 1500,
    'VIP4': 5000,
  };

  // Get current VIP level from user profile
  const currentVipLevel = userProfile?.vipLevel || 'VIP1';
  const walletBalance = userProfile?.balances?.wallet || 0;

  // Helper function to get VIP level number
  const getVipLevelNumber = (level) => {
    return parseInt(level.replace('VIP', ''));
  };

  // Helper function to determine status for each VIP level
  const getVipLevelStatus = (level) => {
    const currentLevelNum = getVipLevelNumber(currentVipLevel);
    const targetLevelNum = getVipLevelNumber(level);
    const requiredAmount = vipRequirements[level];

    if (targetLevelNum < currentLevelNum) {
      return { status: 'Achieved', canUpgrade: false, showUpgrade: false };
    } else if (targetLevelNum === currentLevelNum) {
      return { status: 'Current', canUpgrade: false, showUpgrade: false };
    } else {
      // Higher level - always show upgrade link
      const canUpgrade = walletBalance >= requiredAmount;
      return { status: null, canUpgrade: canUpgrade, showUpgrade: true };
    }
  };

  // Generate VIP levels with dynamic status
  const vipLevels = useMemo(() => {
    return [
      { 
        level: 'VIP1', 
        amount: 'USDT 100', 
        image: '/levels/level-1.png',
        requirement: 100
      },
      { 
        level: 'VIP2', 
        amount: 'USDT 500', 
        image: '/levels/level-2.png',
        requirement: 500
      },
      { 
        level: 'VIP3', 
        amount: 'USDT 1500', 
        image: '/levels/level-3.png',
        requirement: 1500
      },
      { 
        level: 'VIP4', 
        amount: 'USDT 5000', 
        image: '/levels/level-4.png',
        requirement: 5000
      },
    ].map(vip => {
      const { status, canUpgrade, showUpgrade } = getVipLevelStatus(vip.level);
      return {
        ...vip,
        status,
        canUpgrade,
        showUpgrade
      };
    });
  }, [currentVipLevel, walletBalance]);

  const handleUpgrade = (level) => {
    // Show popup when upgrade is clicked
    setSelectedVipLevel(level);
    setShowUpgradePopup(true);
  };

  return (
    <div className="vip-levels-page">
      {/* Top Navigation Bar */}
      <div className="vip-levels-top-bar">
        <div className="vip-levels-back-btn" onClick={onBack}>
          <i className="bi bi-chevron-left"></i>
        </div>
        <div className="vip-levels-title-text">Vip Levels</div>
        <div className="vip-levels-spacer"></div>
      </div>

      {/* VIP Levels List */}
      <div className="vip-levels-page-content">
        <div className="container-fluid px-3 py-4">
          {vipLevels.map((vip, index) => {
            const isCurrent = vip.status === 'Current';
            const isAchieved = vip.status === 'Achieved';
            const showUpgrade = vip.showUpgrade;
            const canUpgrade = vip.canUpgrade;
            
            return (
              <div 
                key={index} 
                className={`vip-level-card-full ${isCurrent ? 'vip-level-current' : ''} ${isAchieved ? 'vip-level-achieved' : ''}`}
              >
                <div className="vip-level-card-icon">
                  <img src={vip.image} alt={vip.level} className="vip-level-card-image" />
                  {isCurrent && (
                    <div className="vip-level-current-indicator">
                      <i className="bi bi-check-circle-fill"></i>
                    </div>
                  )}
                </div>
                <div className="vip-level-card-content">
                  <div className="vip-level-card-header">
                    <span className="vip-level-card-title">{vip.level}</span>
                    {vip.status && (
                      <span className={`vip-level-status-badge vip-level-status-${vip.status.toLowerCase()}`}>
                        {vip.status}
                      </span>
                    )}
                  </div>
                  <div className="vip-level-card-amount">{vip.amount}</div>
                  {showUpgrade && (
                    <div 
                      className={`vip-level-upgrade-link ${!canUpgrade ? 'vip-level-upgrade-disabled' : ''}`}
                      onClick={() => handleUpgrade(vip.level)}
                      title={!canUpgrade ? `Insufficient balance. Required: ${vip.amount}` : `Upgrade to ${vip.level}`}
                      style={{ display: 'block' }}
                    >
                      Upgrade now
                    </div>
                  )}
                  {isAchieved && (
                    <div className="vip-level-achieved-text">
                      <i className="bi bi-check-circle"></i> Achieved
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upgrade Popup */}
      {showUpgradePopup && (
        <ServicePopup onClose={() => setShowUpgradePopup(false)} />
      )}
    </div>
  );
}

export default VIPLevelsPage;

